import React, {
  useRef,
  ReactElement,
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
} from 'react';
import { VariableSizeList as VariableWindowList } from 'react-window';
import { GroupHeadingProps } from './group-heading';
import { GroupHeading as DefaultGroupHeading } from './group-heading';
import { CustomOption } from '../../atoms/select';

type VirtualizationContextType = {
  onHeadingClick: (index: string) => void;
  activeKeys: string[];
  GroupHeading?: React.ComponentType<GroupHeadingProps>;
};
const VirtualizationContext = createContext<VirtualizationContextType | null>(
  null,
);
const flattenGroupedChildrens = (children: ReactElement[]) => {
  return children
    .map((child) => [
      {
        key: child.key,
        props: {
          data: {
            type: 'groupHeading',
            label: child.props?.data?.label,
            options: child.props?.data?.options,
          },
          value: child.props?.getValue(),
          setValue: child.props?.setValue,
        },
      },
      ...(child.props?.children ?? []),
    ])
    .reduce(
      (accumulator, currentValue) => accumulator.concat(currentValue),
      [],
    );
};

// Always include plus 2 with actual height to give gap between options
const optionHeight = {
  groupHeading: 34,
  basic: 34,
};

const computeMenuItemHeight = (
  option: CustomOption & { type: 'groupHeading' | string },
) => {
  if (!option.label) {
    return 0;
  }
  if (option.type === 'groupHeading') {
    return optionHeight.groupHeading;
  }

  return optionHeight.basic;
};

const RowRenderer = ({ index, style, data }) => {
  const context = useContext(VirtualizationContext);
  const GroupHeading = context?.GroupHeading ?? DefaultGroupHeading;

  return (
    <div style={style}>
      {data[index]?.props?.data?.type === 'groupHeading'
        ? GroupHeading && (
            <GroupHeading
              key={data[index]?.key}
              data={data[index]?.props?.data}
              active={context?.activeKeys.includes(data[index]?.key) ?? false}
              onClick={() => context?.onHeadingClick(data[index]?.key)}
              value={data[index]?.props?.value}
              setValue={data[index]?.props?.setValue}
            />
          )
        : data[index]}
    </div>
  );
};

const LOAD_MORE_THRESHOLD = 5;

const VariableListRowRenderer = (props) => {
  const {
    children,
    selectProps: {
      maxMenuHeight,
      grouped,
      isMulti,
      onLoadMore: selectOnLoadMore,
      hasMore: selectHasMore,
      isLoading: selectIsLoading,
    } = {},
    onLoadMore: propsOnLoadMore,
    hasMore: propsHasMore,
    isLoading: propsIsLoading,
  } = props;

  const onLoadMore = propsOnLoadMore ?? selectOnLoadMore;
  const hasMore = propsHasMore ?? selectHasMore;
  const isLoading = propsIsLoading ?? selectIsLoading;

  const [activeKeys, setActiveKeys] = useState<string[]>(
    grouped ? children.map((item) => item.key) : [],
  );

  const onHeadingClick = useCallback(
    (key: string) => {
      const isActive = activeKeys.includes(key);

      setActiveKeys(
        isActive ? activeKeys.filter((i) => i !== key) : [...activeKeys, key],
      );
    },
    [activeKeys],
  );
  const onlyActiveGroupsChildren = useCallback(
    (items) => {
      return items.map((item) => {
        return activeKeys.includes(item.key)
          ? item
          : { ...item, props: { ...item.props, children: [] } };
      });
    },
    [activeKeys],
  );

  const transformedChild = grouped
    ? flattenGroupedChildrens(onlyActiveGroupsChildren(children))
    : children;

  const itemCount = transformedChild.length;
  const listItemCount = isMulti ? itemCount + 1 : itemCount;
  const listRef = useRef<VariableWindowList>(null);

  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }: { visibleStopIndex: number }) => {
      if (
        onLoadMore &&
        hasMore &&
        !isLoading &&
        visibleStopIndex >= listItemCount - LOAD_MORE_THRESHOLD
      ) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, isLoading, listItemCount],
  );

  // Reset cache to make react window recalculate styles once the list of childrens are modified.
  const resetWindowedListCachePosition = () => {
    transformedChild.forEach((child, index) => {
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }
    });
  };

  useEffect(() => {
    resetWindowedListCachePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedChild.length]);

  return (
    <VirtualizationContext.Provider
      value={{
        onHeadingClick,
        activeKeys,
        GroupHeading: props.selectProps.components?.GroupHeading,
      }}
    >
      <VariableWindowList
        height={maxMenuHeight}
        itemCount={listItemCount}
        itemData={transformedChild}
        ref={listRef}
        itemSize={(index: number) => {
          return computeMenuItemHeight(
            transformedChild[index]?.props?.data || [],
          );
        }}
        width="100%"
        onItemsRendered={handleItemsRendered}
      >
        {RowRenderer}
      </VariableWindowList>
    </VirtualizationContext.Provider>
  );
};

export const VirtualizedMenuList = (props): React.JSX.Element => {
  const { children } = props;

  // For handling no options
  if (children && !Array.isArray(children)) {
    return children;
  }

  return <VariableListRowRenderer {...props} />;
};
